import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * A custom element that keeps the "view details" link pointing at the variant on screen.
 * The link is only rendered once, inside a quick add or quick view dialog, so without this the
 * shopper picks a variant and then leaves for the product page on a different one. The variant
 * JSON the picker emits carries no url, so the path comes from the re-rendered block — which is
 * how a combined listing's sibling product is picked up — and the variant id from the event.
 */
class ViewDetailsButton extends Component {
  /** @type {Element | null} - Cached section/dialog the variant listener is attached to. */
  #variantUpdateTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#variantUpdateTarget = this.closest('.shopify-section, dialog');
    this.#variantUpdateTarget?.addEventListener(ThemeEvents.variantUpdate, this.updateLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantUpdateTarget?.removeEventListener(ThemeEvents.variantUpdate, this.updateLink);
  }

  /**
   * Updates the link.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateLink = (event) => {
    const anchor = this.querySelector('a');
    if (!anchor) return;

    const blockId = this.dataset.blockId;
    const rerendered = blockId
      ? event.detail.data.html?.querySelector(`view-details-button[data-block-id="${blockId}"] a`)
      : null;

    const href = rerendered?.getAttribute('href') || anchor.getAttribute('href');
    if (!href) return;

    const url = new URL(href, window.location.origin);
    const variantId = event.detail.resource?.id;

    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    } else {
      url.searchParams.delete('variant');
    }

    anchor.href = `${url.pathname}${url.search}`;
  };
}

if (!customElements.get('view-details-button')) {
  customElements.define('view-details-button', ViewDetailsButton);
}
